import React from 'react'
import { Hotel, Wifi, WifiOff, Clock, Calendar, CheckCircle, Users, DollarSign } from 'lucide-react'
import { getHotelStatus } from '../utils/statusUtils'

const KPICards = ({ hotelData, loading, filters, onFilterChange, bookingData }) => {
  // Calculate realtime metrics from hotel data
  const calculateMetrics = () => {
    console.log('KPICards - hotelData:', hotelData)
    console.log('KPICards - hotelData type:', typeof hotelData)
    console.log('KPICards - hotelData isArray:', Array.isArray(hotelData))
    
    // Handle different data structures
    let hotels = []
    
    if (hotelData) {
      if (Array.isArray(hotelData)) {
        // Direct array
        hotels = hotelData
        console.log('KPICards - Using direct array')
      } else if (hotelData.data && Array.isArray(hotelData.data)) {
        // Pagination structure with data property
        hotels = hotelData.data
        console.log('KPICards - Using pagination data array')
      } else if (hotelData.hotels && hotelData.hotels.data && Array.isArray(hotelData.hotels.data)) {
        // Nested structure like HotelGrid receives
        hotels = hotelData.hotels.data
        console.log('KPICards - Using nested hotels.data array')
      }
    }
    
    if (!Array.isArray(hotels) || hotels.length === 0) {
      console.log('KPICards - No valid hotel data, returning zeros')
      return {
        total_hotels: 0,
        connected_hotels: 0,
        disconnected_hotels: 0,
        avg_response_time: 0
      }
    }

    const total_hotels = hotels.length
    let connected_hotels = 0
    let disconnected_hotels = 0
    let totalResponseTime = 0
    let responseTimeCount = 0

    console.log('KPICards - Processing', total_hotels, 'hotels')

    hotels.forEach((hotel, index) => {
      console.log(`KPICards - Hotel ${index}:`, hotel)
      console.log(`KPICards - Hotel ${index} last_ping:`, hotel.last_ping)
      
      const status = getHotelStatus(hotel.last_ping)
      console.log(`KPICards - Hotel ${index} status:`, status)
      
      if (status === 'connected') {
        connected_hotels++
      } else {
        disconnected_hotels++
      }

      // Calculate response time if available
      if (hotel.avg_response_time && hotel.avg_response_time > 0) {
        totalResponseTime += hotel.avg_response_time
        responseTimeCount++
      }
    })

    const avg_response_time = responseTimeCount > 0 ? totalResponseTime / responseTimeCount : 0

    console.log('KPICards - Final metrics:', {
      total_hotels,
      connected_hotels,
      disconnected_hotels,
      avg_response_time
    })

    return {
      total_hotels,
      connected_hotels,
      disconnected_hotels,
      avg_response_time
    }
  }

  const metrics = calculateMetrics()

  // Handle card click for filtering
  const handleCardClick = (filterType) => {
    if (filterType === 'connected' || filterType === 'disconnected') {
      const newStatus = filters?.status === filterType ? 'all' : filterType
      onFilterChange?.({
        ...filters,
        status: newStatus
      })
    }
  }

  const cards = [
    {
      title: 'Total Hotels',
      value: metrics.total_hotels,
      icon: Hotel,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      clickable: false,
    },
    {
      title: 'Connected',
      value: metrics.connected_hotels,
      icon: Wifi,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      clickable: true,
      filterType: 'connected',
      isActive: filters?.status === 'connected',
    },
    {
      title: 'Disconnected',
      value: metrics.disconnected_hotels,
      icon: WifiOff,
      color: 'bg-red-500',
      bgColor: 'bg-red-50',
      clickable: true,
      filterType: 'disconnected',
      isActive: filters?.status === 'disconnected',
    }
  ]

  // Always show booking summary card (use 0 if data not available yet)
  {
    const bookingCards = [
      {
        title: 'Today\'s Bookings',
        value: (bookingData?.today_bookings ?? 0),
        icon: Calendar,
        color: 'bg-indigo-500',
        bgColor: 'bg-indigo-50',
        clickable: false,
      },
    ]
    cards.push(...bookingCards)
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="card animate-pulse">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              <div className="ml-4 flex-1">
                <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filter Status Message */}
      {filters?.status && filters.status !== 'all' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-sm font-medium text-blue-800">
                Menampilkan hotel dengan status: <span className="capitalize font-bold">{filters.status}</span>
              </span>
            </div>
            <button
              onClick={() => onFilterChange?.({ ...filters, status: 'all' })}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Hapus Filter
            </button>
          </div>
        </div>
      )}
      
      {/* KPI Cards Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${bookingData ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6`}>
        {cards.map((card, index) => {
          const Icon = card.icon
          const isClickable = card.clickable
          const isActive = card.isActive
          
          return (
            <div 
              key={index} 
              className={`card transition-all duration-200 ${
                isClickable 
                  ? 'cursor-pointer hover:shadow-lg hover:scale-105 active:scale-95' 
                  : 'hover:shadow-md'
              } ${
                isActive 
                  ? 'ring-2 ring-blue-500 shadow-lg' 
                  : ''
              }`}
              onClick={() => isClickable && handleCardClick(card.filterType)}
              title={isClickable ? `Klik untuk filter hotel ${card.title.toLowerCase()}` : ''}
            >
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${card.bgColor} ${isActive ? 'ring-2 ring-white' : ''}`}>
                  <Icon className={`w-6 h-6 ${card.color.replace('bg-', 'text-')}`} />
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-gray-600'}`}>
                      {card.title}
                    </p>
                    {isClickable && (
                      <div className="text-xs text-gray-400">
                        {isActive ? '✓' : '👆'}
                      </div>
                    )}
                  </div>
                  <p className={`text-2xl font-bold ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                    {card.value}
                  </p>
                  {isClickable && (
                    <p className="text-xs text-gray-500 mt-1">
                      {isActive ? 'Filter aktif' : 'Klik untuk filter'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default KPICards